import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from "@remix-run/react";
import { Header } from './Header';
import { centralEuropeanNow } from '../../../../play-core/utils/date-helper';
import { loadTvLivestreams } from '../../api/load-tv-livestream';
import { loadTvProgramGuide } from '../../api/load-tv-program-guide';
import { loadTvProgramGuideNonSrg } from '../../api/load-tv-program-guide-non-srg';
import { queryKeys } from '../../config/use-query';
import { usePath, useVideoConfig } from '../../config/VideoAppContext';
import { useAxios } from '../../hooks/use-axios';
import { allowEscRoute, escSecondLevelNaviLink } from '../../pages/EscPage/EscPage.route';
import { liveEventsSecondLevelNaviLink } from '../../pages/LiveEvent/LiveEventsPage.route';
import { isSelectedTopicRoute } from '../../pages/Topic/TopicPage.route';
import { liveSecondLevelNaviLinks } from '../../pages/TvLive/TvLivePage.route';
import { useDataState } from '../../providers/DataProvider/DataContext';
import { isoDate } from '../../utils/date-formatter';
import { jsx as _jsx } from "react/jsx-runtime";
export const DefaultHeader = ({
  route,
  initialData
}) => {
  const axios = useAxios();
  const {
    t,
    i18n
  } = useTranslation();
  const {
    header,
    tvGuide,
    liveEvents,
    baseUrl,
    businessUnit,
    serverEnvironment
  } = useVideoConfig();
  const {
    livePath,
    showOverviewPath,
    topicPath,
    tvGuidePath,
    tvGuideByChannelPath
  } = usePath();
  const {
    topicName
  } = useParams();
  const {
    topicState: {
      topics
    }
  } = useDataState();
  const {
    data: livestreams,
    isLoading: isLivestreamLoading
  } = useQuery({
    queryKey: [queryKeys.LiveTvLivestream, axios],
    queryFn: () => loadTvLivestreams(axios)
  });
  const providers = tvGuide?.tvGuideProviders ?? [];
  const currentDate = isoDate(centralEuropeanNow());
  const {
    data: programGuide,
    isLoading: isProgramGuideLoading
  } = useQuery({
    queryKey: [queryKeys.TvProgramGuide(currentDate, providers[0]), axios],
    queryFn: () => providers[0] === 'non-srg' ? loadTvProgramGuideNonSrg(currentDate, axios, undefined) : loadTvProgramGuide(currentDate, axios, [providers[0]], true),
    enabled: providers.length > 0,
    initialData: initialData.tvGuide?.[queryKeys.TvProgramGuide(currentDate, providers[0])]
  });
  const liveLinks = useMemo(() => liveSecondLevelNaviLinks(i18n, livestreams ?? [], baseUrl), [i18n, livestreams, baseUrl]);
  const mainNavigation = [{
    title: t('global.shows'),
    path: showOverviewPath
  }];
  const showLivestreams = livestreams?.[0] || isLivestreamLoading;
  if (showLivestreams) {
    mainNavigation.push({
      title: t('global.livestreams'),
      path: livePath(livestreams?.[0] || {
        id: '',
        title: ''
      })
    });
  }
  if (tvGuide) {
    mainNavigation.push({
      title: t('global.tvGuide'),
      path: tvGuidePath()
    });
  }
  const topicLinks = topics.map(topic => ({
    title: topic.title,
    path: topicPath(topic.title),
    isActive: r => isSelectedTopicRoute(r, topic.title, topicName ?? '')
  }));
  const secondLevelNavigationShows = {
    title: t('global.shows'),
    links: [{
      title: t('global.aToZ'),
      path: showOverviewPath,
      isActive: r => r.pageId === 'showOverview'
    }, ...topicLinks]
  };
  const liveSecondLevelNavi = {
    title: t('global.livestreams'),
    links: liveEvents ? [...liveLinks, liveEventsSecondLevelNaviLink(i18n, baseUrl)] : liveLinks
  };
  if (businessUnit === 'srf') {
    if (allowEscRoute(serverEnvironment)) {
      liveSecondLevelNavi.links.push(escSecondLevelNaviLink(baseUrl));
    }
  }
  const tvGuideSecondLevelNavi = {
    title: t('global.tvGuide'),
    links: [{
      title: t('tvGuide.programOverview'),
      path: tvGuidePath(),
      isActive: r => r.pageId === 'tvGuide'
    }]
  };
  const showTvGuideChannelLink = isProgramGuideLoading || programGuide?.[0]?.channel;
  if (showTvGuideChannelLink) {
    tvGuideSecondLevelNavi.links.push({
      title: t('tvGuide.channels'),
      path: tvGuideByChannelPath(isoDate(centralEuropeanNow()), programGuide?.[0]?.channel || {
        urn: '',
        title: '-'
      }),
      isActive: r => r.pageId === 'tvGuideByChannel'
    });
  }
  const pageIdToSecondLevelNavigation = {
    topic: secondLevelNavigationShows,
    showOverview: secondLevelNavigationShows,
    liveTV: liveSecondLevelNavi,
    liveEvents: liveSecondLevelNavi,
    esc: liveSecondLevelNavi,
    tvGuide: tvGuideSecondLevelNavi,
    tvGuideByChannel: tvGuideSecondLevelNavi
  };
  const secondLevelNavigation = pageIdToSecondLevelNavigation[route.pageId];
  const config = {
    menuEntries: header.menuEntries,
    loginEntries: header.loginEntries,
    mainNavigation,
    secondLevelNavigation
  };
  return /*#__PURE__*/_jsx(Header, {
    config: config,
    route: route
  });
};